import React, { useState, useEffect } from "react";
import "./Resources.scss";
import axios from "axios";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const Resources = () => {
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchDocuments();
  }, []);

  const refreshToken = async () => {
    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await axios.post(`${BE_URL}/refreshToken`, {
        token: tokens.refreshToken,
      });

      if (response.data) {
        localStorage.setItem("ERPTokens", JSON.stringify(response.data));
        return response.data.accessToken;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      alert("Session expired. Please login again.");
      return null;
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
      const response = await axios.get(`${BE_URL}/api/general/resources`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });

      setDocuments(response.data);

      // Extract unique categories
      const uniqueCategories = [
        ...new Set(response.data.map((doc) => doc.category || "Uncategorized")),
      ];
      setCategories(uniqueCategories);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        const newAccessToken = await refreshToken();
        if (newAccessToken) {
          return fetchDocuments();
        } else {
          setError("Authentication failed. Please log in again.");
        }
      } else {
        setError(error.message || "Failed to fetch documents.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Format file size into readable format
  const formatFileSize = (bytes) => {
    if (!bytes) return "Unknown size";

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(2)} KB`;
    } else {
      const mb = kb / 1024;
      return `${mb.toFixed(2)} MB`;
    }
  };

  // Get appropriate icon based on file type
  const getFileIcon = (fileName) => {
    if (!fileName) return "📄";

    const extension = fileName.split(".").pop().toLowerCase();

    switch (extension) {
      case "pdf":
        return "📕";
      case "doc":
      case "docx":
        return "📘";
      case "xls":
      case "xlsx":
        return "📗";
      case "ppt":
      case "pptx":
        return "📙";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "🖼️";
      case "zip":
      case "rar":
        return "🗜️";
      case "txt":
        return "📝";
      case "mp4":
      case "avi":
      case "mov":
        return "🎬";
      case "mp3":
      case "wav":
        return "🎵";
      default:
        return "📄";
    }
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown date";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="resources">
      <div className="resources-header">
        <div className="resources-title">
          <h1>Resources</h1>
          <p>Access and download all available resources</p>
        </div>

        <div className="resources-actions">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="search-icon">
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </span>
          </div>
        </div>
      </div>

      <div className="resources-container">
        <div className="resources-sidebar">
          <div className="category-filter">
            <h3>Categories</h3>
            <ul>
              <li
                className={selectedCategory === "all" ? "active" : ""}
                onClick={() => setSelectedCategory("all")}
              >
                All Resources
              </li>
              {categories.map((category) => (
                <li
                  key={category}
                  className={selectedCategory === category ? "active" : ""}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="resources-content">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading resources...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={fetchDocuments}>Try Again</button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="no-resources">
              <div className="no-data-icon">📚</div>
              <p>No resources found matching your criteria</p>
              {searchTerm && (
                <button onClick={() => setSearchTerm("")}>Clear Search</button>
              )}
            </div>
          ) : (
            <div className="documents-grid">
              {filteredDocuments.map((doc) => (
                <div key={doc.id} className="document-card">
                  <div className="document-icon">{getFileIcon(doc.name)}</div>
                  <div className="document-info">
                    <h3 className="document-name">{doc.name}</h3>
                    <div className="document-meta">
                      <span className="document-size">
                        {formatFileSize(doc.size_bytes)}
                      </span>
                      <span className="document-date">
                        {formatDate(doc.created_at)}
                      </span>
                    </div>
                    {doc.description && (
                      <p className="document-description">{doc.description}</p>
                    )}
                    {doc.category && (
                      <span className="document-category">{doc.category}</span>
                    )}
                  </div>
                  <div className="document-actions">
                    <a
                      href={doc.s3_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-button"
                    >
                      <span className="download-icon">
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </span>
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Resources;
